import {
  IconChevronDown,
  IconChevronUp,
  IconSelector,
} from '@tabler/icons-react';
import fileDownload from 'js-file-download';
import type {FC} from 'react';
import {memo, useCallback, useMemo, useState} from 'react';
import type {AssistantTableResponse} from 'src/api/ProactApiClient';
import {AssistantTableColumnFormatEnum} from 'src/api/ProactApiClient';
import {
  CSV_MIME_TYPE,
  buildTableCsv,
  scoutCsvFileName,
} from 'src/app/assistant/assistantCsv';
import {useAssistantQueryCsvDownload} from 'src/app/assistant/useAssistantQueryCsvDownload';
import {Box} from 'src/design/atoms/Box';
import {Button} from 'src/design/atoms/Button';
import {Group} from 'src/design/atoms/Group';
import {InfoIcon, IconSize} from 'src/design/atoms/icons/CommonIcons';
import {Stack} from 'src/design/atoms/Stack';
import {Table} from 'src/design/atoms/Table';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import type {ChatTableSortDirection} from './chatTableFormat';
import {
  NUMERIC_CELL_STYLE,
  TABLE_COLUMN_MIN_WIDTH,
  TABLE_EMPTY_TEXT,
  TABLE_SORTED_NOTE,
  formatTableCell,
  isNumericTableFormat,
  normalizeTableRows,
  sortTableRows,
  tableTruncationText,
} from './chatTableFormat';

export interface ChatTableProps {
  table: AssistantTableResponse;
  /** With `canExport`, a truncated table downloads its full query result instead of the shown rows. */
  conversationId?: number;
  /** False while the reply streams: the query is downloadable only once the reply is saved. */
  canExport?: boolean;
}

interface SortState {
  columnIndex: number;
  direction: ChatTableSortDirection;
}

const TABLE_MAX_HEIGHT = 360;
const FULL_RESULT_CSV_TOOLTIP =
  'Runs the query again with your current access: all its columns and rows, up to 1,000.';

const ARIA_SORT: Record<ChatTableSortDirection, 'ascending' | 'descending'> = {
  asc: 'ascending',
  desc: 'descending',
};

const nextSort = (
  current: SortState | null,
  columnIndex: number
): SortState | null => {
  if (current?.columnIndex !== columnIndex)
    return {columnIndex, direction: 'asc'};
  return current.direction === 'asc' ? {columnIndex, direction: 'desc'} : null;
};

const SortIcon: FC<{direction: ChatTableSortDirection | null}> = ({
  direction,
}) => {
  const props = {size: 12, 'aria-hidden': true, style: {flexShrink: 0}};
  if (direction === 'asc') return <IconChevronUp {...props} />;
  if (direction === 'desc') return <IconChevronDown {...props} />;
  return <IconSelector {...props} />;
};

const FullResultCsvButton: FC<{
  conversationId: number;
  toolUseId: string;
  label: string;
}> = ({conversationId, toolUseId, label}) => {
  const {download, downloading} = useAssistantQueryCsvDownload(
    conversationId,
    toolUseId
  );
  return (
    // Button does not forward refs, so the Tooltip anchors on a Box.
    <Tooltip label={FULL_RESULT_CSV_TOOLTIP}>
      <Box component="span" display="inline-block">
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          aria-label={`Download full result of ${label} as CSV`}
          loading={downloading}
          onClick={() => void download()}>
          Download full result (CSV)
        </Button>
      </Box>
    </Tooltip>
  );
};

/**
 * Scout table with format-aware cells, click-to-sort headers (ascending, descending, original),
 * a truncation notice, and a CSV download of the shown rows or, for a truncated saved reply, the full query result.
 * @registry Renders a Scout render_table result: sortable, formatted, downloadable as CSV.
 */
export const ChatTable: FC<ChatTableProps> = memo(function ChatTable({
  table,
  conversationId,
  canExport = false,
}) {
  const [sort, setSort] = useState<SortState | null>(null);
  const columns = useMemo(() => table.columns ?? [], [table.columns]);
  const numericColumns = useMemo(
    () => columns.map(column => isNumericTableFormat(column.format)),
    [columns]
  );
  const rows = useMemo(() => normalizeTableRows(table), [table]);
  const shownRows = useMemo(
    () =>
      sort
        ? sortTableRows(
            rows,
            sort.columnIndex,
            columns[sort.columnIndex]?.format ??
              AssistantTableColumnFormatEnum.Text,
            sort.direction
          )
        : rows,
    [rows, columns, sort]
  );
  const label = table.title || 'table';
  const fullResultToolUseId =
    table.truncated && canExport && conversationId !== undefined
      ? table.queryToolUseId
      : undefined;

  const downloadShownRows = useCallback(
    () =>
      fileDownload(
        buildTableCsv(table, shownRows),
        scoutCsvFileName('table', new Date()),
        CSV_MIME_TYPE
      ),
    [table, shownRows]
  );

  return (
    <Stack gap="xs" w="100%" style={{minWidth: 0}}>
      {table.title && (
        <Text fw={600} size="sm">
          {table.title}
        </Text>
      )}
      {rows.length === 0 ? (
        !table.truncated && (
          <Text size="xs" c="dimmed">
            {TABLE_EMPTY_TEXT}
          </Text>
        )
      ) : (
        <Table.ScrollContainer
          minWidth={columns.length * TABLE_COLUMN_MIN_WIDTH}
          maxHeight={TABLE_MAX_HEIGHT}>
          <Table withTableBorder stickyHeader fz="xs" verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                {columns.map((column, columnIndex) => {
                  const numeric = numericColumns[columnIndex];
                  const direction =
                    sort?.columnIndex === columnIndex ? sort.direction : null;
                  return (
                    <Table.Th
                      key={columnIndex}
                      ta={numeric ? 'right' : undefined}
                      aria-sort={direction ? ARIA_SORT[direction] : 'none'}>
                      <UnstyledButton
                        onClick={() =>
                          setSort(current => nextSort(current, columnIndex))
                        }
                        style={{fontSize: 'inherit', fontWeight: 'inherit'}}>
                        <Group
                          gap={4}
                          wrap="nowrap"
                          justify={numeric ? 'flex-end' : 'flex-start'}>
                          <Text span inherit>
                            {column.label}
                          </Text>
                          <SortIcon direction={direction} />
                        </Group>
                      </UnstyledButton>
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {shownRows.map((row, rowIndex) => (
                <Table.Tr key={rowIndex}>
                  {columns.map((column, columnIndex) => {
                    const numeric = numericColumns[columnIndex];
                    return (
                      <Table.Td
                        key={columnIndex}
                        ta={numeric ? 'right' : undefined}
                        style={numeric ? NUMERIC_CELL_STYLE : undefined}>
                        {formatTableCell(
                          row[columnIndex] ?? null,
                          column.format
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {table.truncated && (
        <Group gap={4} wrap="nowrap" align="flex-start">
          <InfoIcon size={IconSize.sm} style={{flexShrink: 0, marginTop: 2}} />
          <Text size="xs" c="dimmed">
            {tableTruncationText(rows.length)}
            {sort && ` ${TABLE_SORTED_NOTE}`}
          </Text>
        </Group>
      )}
      {(fullResultToolUseId || rows.length > 0) && (
        <Group gap="xs">
          {conversationId !== undefined && fullResultToolUseId ? (
            <FullResultCsvButton
              conversationId={conversationId}
              toolUseId={fullResultToolUseId}
              label={label}
            />
          ) : (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              aria-label={`Download CSV of ${label}`}
              onClick={downloadShownRows}>
              Download CSV
            </Button>
          )}
        </Group>
      )}
    </Stack>
  );
});
